import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft,
  ChevronRight,
  Clock,
  ThumbsUp,
  Share2,
  CheckCircle2,
  Monitor,
  Smartphone,
  Puzzle,
  Pin,
  MessageCircle,
  ShieldCheck,
  RefreshCw
} from "lucide-react";

export default function WhatsAppExtensionTutorialPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900 font-sans pb-20">
      <header className="border-b bg-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex flex-col md:flex-row justify-between items-center gap-4">
          <Link to="/">
            <img
              src="/marketing/logo_official.png"
              className="h-28 object-contain"
              alt="OdontoAssist AI Logo"
            />
          </Link>
          <div className="flex items-center gap-6">
            <Link
              to="/ajuda/tutoriais"
              className="text-gray-500 hover:text-indigo-600 font-semibold hidden md:block"
            >
              Central de Guias
            </Link>
            <Link
              to="/"
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-2xl font-black tracking-wide flex items-center gap-2 transition-all shadow-lg hover:-translate-y-1"
            >
              <ArrowLeft size={20} />
              Voltar {user ? "para o Aplicativo" : "ao Início"}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 mb-6">
          <Link to="/ajuda" className="hover:text-indigo-600">Ajuda</Link>
          <ChevronRight size={14} className="text-gray-400" />
          <Link to="/ajuda/tutoriais" className="hover:text-indigo-600">Guias</Link>
          <ChevronRight size={14} className="text-gray-400" />
          <span className="text-indigo-600">Integrações</span>
        </div>

        <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-4 leading-tight">
          Como instalar, fixar e usar a Extensão do WhatsApp Web
        </h1>

        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500 mb-8">
          <div className="flex items-center gap-2">
            <Clock size={16} /> <span>5 min de leitura / vídeo</span>
          </div>
          <div className="flex items-center gap-2 bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-bold uppercase tracking-wider text-xs">
            Produtividade e Atendimento
          </div>
        </div>

        <div className="w-full aspect-video rounded-3xl overflow-hidden relative shadow-2xl mb-12 border-4 border-white bg-black">
          <iframe
            width="100%"
            height="100%"
            src="https://www.youtube.com/embed/WEuZ3QVZaDc"
            title="Como instalar e usar a extensão do WhatsApp Web"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            className="absolute top-0 left-0 w-full h-full"
          ></iframe>
        </div>

        <div className="flex items-center justify-between border-b border-gray-200 pb-8 mb-10">
          <div className="flex items-center gap-4">
            <img
              src="https://i.pravatar.cc/150?img=12"
              alt="Instrutor"
              className="w-12 h-12 rounded-full border-2 border-white shadow-sm"
            />
            <div>
              <p className="font-bold text-gray-900">Equipe OdontoAssist AI</p>
              <p className="text-sm text-gray-500">Implantação e Suporte</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 transition-colors">
              <ThumbsUp size={18} /> Útil
            </button>
            <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 transition-colors">
              <Share2 size={18} /> Compartilhar
            </button>
          </div>
        </div>

        <article className="prose prose-indigo prose-lg max-w-none text-gray-600 text-justify">
          <p className="lead text-xl text-gray-700 font-medium">
            A extensão do OdontoAssist AI foi criada para ajudar sua equipe a consultar rapidamente dados do paciente enquanto usa o WhatsApp Web, copiar mensagens prontas e acessar ações rápidas como prontuário e agendamento.
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mt-8">
            <p className="text-amber-800 text-sm mb-0">
              <strong>Importante:</strong> esta extensão foi pensada para uso em computador, no navegador, junto com o <strong>WhatsApp Web</strong>. Em celular, iPhone, Android e PWA do aplicativo, o fluxo ideal continua sendo abrir o WhatsApp normal.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Instalação no Google Chrome</h2>

          <div className="space-y-8 mt-8">
            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">1</span>
                <Puzzle className="text-indigo-600" size={20} />
                Acesse a loja do Chrome
              </h3>
              <p>
                Abra o Google Chrome e acesse diretamente a página oficial da extensão na Chrome Web Store:
              </p>

              <a
                href="https://chromewebstore.google.com/detail/donigdgkngpmndgkfajpfokgofjpolhh?utm_source=item-share-cb"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-4 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-bold shadow-lg transition-all"
              >
                Instalar extensão no Chrome
              </a>

              <p className="mt-4 text-sm text-gray-500">
                Caso a extensão ainda não apareça na busca da loja, utilize o link direto acima.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">2</span>
                <CheckCircle2 className="text-indigo-600" size={20} />
                Clique em “Usar no Chrome”
              </h3>
              <p>
                Na página da extensão, clique em <strong>Usar no Chrome</strong> e depois confirme em <strong>Adicionar extensão</strong>.
                O navegador fará a instalação automaticamente.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black">3</span>
                <Pin className="text-indigo-600" size={20} />
                Fixe a extensão na barra do navegador
              </h3>
              <p>
                No canto superior direito do Chrome, clique no ícone de extensões (peça de quebra-cabeça).
                Depois localize a extensão do OdontoAssist AI e clique no ícone de <strong>fixar</strong>.
                Isso facilita o acesso rápido sempre que estiver usando o WhatsApp Web.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-4">Instalação no Microsoft Edge</h2>

          <div className="space-y-8 mt-8">
            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-emerald-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-black">1</span>
                <Monitor className="text-emerald-600" size={20} />
                Acesse a loja do Edge
              </h3>
              <p>
                No Microsoft Edge, você poderá instalar a extensão diretamente pela loja oficial.
              </p>

              <a
                href="https://microsoftedge.microsoft.com/addons/detail/odontoassist-connect/kjpjedcjdhphbcklhnngejhippiiohco"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-4 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-bold shadow-lg transition-all"
              >
                Instalar extensão no Edge
              </a>

              <p className="mt-4 text-sm text-gray-500">
                Caso não apareça na busca da loja, utilize o link direto acima.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-emerald-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-black">2</span>
                <CheckCircle2 className="text-emerald-600" size={20} />
                Clique em “Obter” ou “Instalar”
              </h3>
              <p>
                Confirme a instalação quando o Edge solicitar. Depois disso, a extensão ficará disponível no navegador.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-emerald-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-black">3</span>
                <Pin className="text-emerald-600" size={20} />
                Fixe a extensão no Edge
              </h3>
              <p>
                Clique no ícone de extensões no canto superior do navegador e fixe a extensão do OdontoAssist AI para acesso rápido.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-4">Como usar no dia a dia</h2>

          <div className="space-y-8 mt-8">
            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-rose-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center text-sm font-black">1</span>
                <MessageCircle className="text-rose-600" size={20} />
                Abra o WhatsApp Web
              </h3>
              <p>
                Entre normalmente no <strong>WhatsApp Web</strong>. A extensão foi feita para atuar ao lado da conversa aberta no navegador.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-rose-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center text-sm font-black">2</span>
                <RefreshCw className="text-rose-600" size={20} />
                Selecione a conversa do paciente
              </h3>
              <p>
                Ao clicar em uma conversa, a extensão identifica o contato atual e tenta localizar o paciente correspondente no OdontoAssist.
                Essa busca automática acontece uma vez por troca de conversa.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-rose-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center text-sm font-black">3</span>
                <ShieldCheck className="text-rose-600" size={20} />
                Confirme ou troque o paciente se necessário
              </h3>
              <p>
                Se a extensão localizar o paciente correto, você já poderá usar os botões de acesso rápido.
                Se a associação automática vier errada, use o campo <strong>Buscar ou confirmar paciente</strong> para procurar outro paciente manualmente.
              </p>
            </div>

            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-2 h-full bg-rose-500" />
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center text-sm font-black">4</span>
                <Monitor className="text-rose-600" size={20} />
                Use os atalhos
              </h3>
              <p>
                Depois que o paciente estiver correto, a equipe poderá:
              </p>
              <ul className="mt-3 space-y-2">
                <li>Abrir o prontuário</li>
                <li>Criar novo agendamento</li>
                <li>Remarcar agendamento</li>
                <li>Atualizar o status do atendimento</li>
                <li>Copiar mensagens rápidas</li>
              </ul>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-4">Quando usar os botões do OdontoAssist</h2>

          <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
            <p>
              Dentro do OdontoAssist AI, algumas páginas possuem o botão <strong>Abrir WhatsApp</strong> ou <strong>Abrir WhatsApp Web</strong>.
              No computador, esse botão leva ao WhatsApp Web com contexto para facilitar o atendimento.
              No celular e no PWA, o comportamento pode ser diferente, abrindo o WhatsApp normal quando fizer mais sentido.
            </p>
          </div>

          <div className="bg-indigo-900 rounded-3xl p-8 mt-12 text-white">
            <h3 className="text-2xl font-bold mb-4">Boas práticas de uso</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-indigo-300 shrink-0 mt-1" size={20} />
                <span>Fixe a extensão no navegador da recepção ou do computador principal da clínica.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-indigo-300 shrink-0 mt-1" size={20} />
                <span>Sempre confira se o paciente selecionado na extensão está correto antes de abrir prontuário ou alterar agendamento.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-indigo-300 shrink-0 mt-1" size={20} />
                <span>Quando a identificação automática não estiver correta, use a confirmação manual para evitar vinculação errada.</span>
              </li>
            </ul>
          </div>
        </article>
      </main>
    </div>
  );
}
